import React, { useEffect, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import DownloadButtons from "../DownloadButtons/DownloadButtons";
import EntriesSelector from "../Pagination/EntriesSelector";
import Pagination from "../Pagination/Paginations";
import { API_BASE_URL } from "../../config";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/* month_name may come as "January" / "january" / "Jan" / "01" -> normalise to first 3 letters */
const monthKey = (name) => {
  const raw = String(name ?? "").trim();
  if (/^\d+$/.test(raw)) {
    const m = MONTHS[Number(raw) - 1];
    return m ? m.toLowerCase().slice(0, 3) : "";
  }
  return raw.toLowerCase().slice(0, 3);
};

/* stored rows look like { month: "Jan-2026", leave: 1 } -> take whichever key is present */
const countValue = (h) =>
  Number(h.leave ?? h.count ?? h.leave_count ?? h.total ?? h.no_of_days ?? h.days ?? 0) || 0;

/**
 * unavailable_leave_history is stored on xin_employees as a JSON value, so it can
 * reach the UI as a real array, a JSON string, or an object keyed by month.
 * Normalise all three to [{ month_name, count }].
 */
const parseHistory = (value) => {
  if (!value) return [];

  let data = value;

  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      return [];
    }
  }

  if (Array.isArray(data)) return data;

  // { "January": 2, "March": 1 }  or  { "January": { count: 2 } }
  if (typeof data === "object") {
    return Object.keys(data).map((k) => {
      const v = data[k];
      return v && typeof v === "object"
        ? { month_name: v.month_name || k, count: countValue(v) }
        : { month_name: k, count: Number(v) || 0 };
    });
  }

  return [];
};

/* build { jan: 2, mar: 1, ... } from the history */
const buildMonthCounts = (history) => {
  const counts = {};
  parseHistory(history).forEach((h) => {
    const key = monthKey(h.month_name ?? h.month);   // "Jan-2026" -> "jan"
    if (!key) return;
    counts[key] = (counts[key] || 0) + countValue(h);
  });
  return counts;
};

const NoLeaveReport = () => {

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // ✅ Load report by default (no filters)
  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const res = await axios.post(
          API_BASE_URL + "/withoutleave",
          {},
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            },
          }
        );

        const payload = res.data;
        const list = Array.isArray(payload)
          ? payload
          : payload.records || payload.employees || payload.data || [];

        setRecords(Array.isArray(list) ? list : []);
      } catch (error) {
        toast.error("Error fetching report", { toastId: "no-leave-error" });
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, []);

  /* ✅ Only rows that actually have unavailable_leave_history */
  const rows = records
    .map((item) => {
      const monthCounts = buildMonthCounts(item.unavailable_leave_history);

      return {
        ...item,
        monthCounts,
        // API sends total_count, fall back to summing the months
        totalCount:
          Number(item.total_count) ||
          Object.values(monthCounts).reduce((sum, n) => sum + n, 0),
        unavailableLeave: Number(item.unavailable_leave) || 0,
      };
    })
    .filter((item) => Object.keys(item.monthCounts).length > 0);

  const filteredRecordsAll = rows.filter((item) =>
    `${item.employee_id} ${item.employee_name} ${item.department_name || item.department}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* Pagination (Previous / Next style) */
  const totalPages = Math.ceil(filteredRecordsAll.length / perPage);
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const filteredRecords = filteredRecordsAll.slice(indexOfFirst, indexOfLast);


  const exportData = filteredRecordsAll.map((row) => {
    const item = {
      employee_id: row.employee_id,
      employee_name: row.employee_name,
      department: row.department_name || row.department,
    };
    MONTHS.forEach((m) => {
      item[m] = row.monthCounts[monthKey(m)] || 0;
    });
    item["Total Count"] = row.totalCount;
    item["Unavailable Leave"] = row.unavailableLeave;
    return item;
  });


  return (
    <div className="container-fluid mt-4">
      <div className="card">
        <h6 className="card-header d-flex align-items-center" style={{ color: "#1d3a8a", fontWeight: 600 }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
            <path fillRule="evenodd" d="M12.5 16a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7m1.679-4.493-1.335 2.226a.75.75 0 0 1-1.174.144l-.774-.773a.5.5 0 0 1 .708-.708l.547.548 1.017-1.694a.5.5 0 1 1 .858.257z"/>
            <path d="M11 5a3 3 0 1 1-6 0 3 3 0 0 1 6 0M8 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4"/>
            <path d="M8.256 14a4.5 4.5 0 0 1-.229-1.004H3c.001-.246.154-.986.832-1.664C4.484 10.68 5.711 10 8 10q.39 0 .74.025c.226-.341.496-.65.804-.918Q8.844 9.002 8 9c-5 0-6 3-6 4s1 1 1 1z"/>
          </svg>
          Unavailed Leave List
        </h6>

        <div className="card-body">

          <div className="d-flex justify-content-between mb-3 align-items-center">
            <DownloadButtons
              data={exportData}
              filename="Unavailed_leave"
            />

            <div className="d-flex align-items-center" style={{ gap: "10px" }}>
              <EntriesSelector
                entriesPerPage={perPage}
                setEntriesPerPage={setPerPage}
                setCurrentPage={setCurrentPage}
              />
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search..."
                style={{ width: "220px" }}
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              />
            </div>
          </div>


          {/* Header is always rendered, counts fill each month cell */}
          <div className="table-responsive sticky-table-wrap">
            <table className="table table-bordered text-center text-nowrap mb-0 sticky-table sticky-table-shaded">
              <thead>
                <tr>
                  <th style={{ whiteSpace: "nowrap" }}>Employee ID</th>
                  <th style={{ whiteSpace: "nowrap" }}>Employee Name</th>
                  <th style={{ whiteSpace: "nowrap" }}>Department</th>
                  {MONTHS.map((m) => (
                    <th key={m} style={{ whiteSpace: "nowrap" }}>
                      {m.slice(0, 3)}
                    </th>
                  ))}
                  <th style={{ whiteSpace: "nowrap" }}>Total Count</th>
                  {/* <th style={{ whiteSpace: "nowrap" }}>Unavailable Leave</th> */}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5 + MONTHS.length} className="text-center p-3">
                      Loading...
                    </td>
                  </tr>
                ) : filteredRecords.length === 0 ? (
                  <tr>
                    <td colSpan={5 + MONTHS.length} className="text-center p-3 text-danger fw-bold">
                      No records found
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((row, idx) => (
                    <tr key={row.employee_id || idx}>
                      <td>{row.employee_id}</td>
                      <td>{row.employee_name}</td>
                      <td>{row.department_name || row.department}</td>
                      {MONTHS.map((m) => (
                        <td key={m}>{row.monthCounts[monthKey(m)] || 0}</td>
                      ))}
                      <td className="fw-bold">{row.totalCount}</td>
                      {/* <td>{row.unavailableLeave}</td> */}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />

        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default NoLeaveReport;
